import { useState, useRef } from 'react';
import { Search, FileSearch, Clock, Shield, Hash, Globe, Activity, AlertCircle, ChevronDown } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { adminComplianceLogLookup } from '../../services/api';
import AdminDashboardNav from '../../components/dashboard/AdminDashboardNav';

interface ComplianceRecord {
  id: string;
  createdAt: string;
  ipAddress: string;
  action: string;
  principal: string;
  description: string;
}

const ACTION_COLORS: Record<string, string> = {
  PLATFORM_MARKETING_OPT_IN: 'bg-green-500/20 text-green-400 border-green-500/30',
  PLATFORM_MARKETING_OPT_OUT: 'bg-red-500/20 text-red-400 border-red-500/30',
  ARTIST_TRANSMISSION_OPT_IN: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  ARTIST_TRANSMISSION_OPT_OUT: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  TENANT_CREATED: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  MEMBERSHIP_JOINED: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  NOTIFICATION_MUTED: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  UNSUBSCRIBED: 'bg-red-500/20 text-red-300 border-red-500/30',
};

function getActionColor(action: string): string {
  return ACTION_COLORS[action] || 'bg-white/10 text-text-secondary border-white/20';
}

export default function AdminComplianceAuditPage() {
  const { token } = useAuthStore();
  const [email, setEmail] = useState('');
  const [results, setResults] = useState<ComplianceRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setLoading(true);
    setError('');
    setResults(null);
    setSearched(false);

    try {
      const res = await adminComplianceLogLookup(trimmed, token!);
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Request failed' }));
        setError((body as any).error || `Error ${res.status}`);
      } else {
        const json = await res.json() as { data: ComplianceRecord[]; count: number };
        setResults(json.data);
      }
    } catch {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const formatDate = (ts: string) => {
    const date = new Date(ts);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <AdminDashboardNav currentPage="audit" />

      <main className="pt-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2 animate-fade-in-up">
          <FileSearch className="w-6 h-6 text-electric-cyan" />
          <h1 className="text-2xl font-bold">Compliance Audit</h1>
        </div>
        <p className="text-sm text-text-secondary mb-8 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
          Search the zero-knowledge ledger by email. The address is hashed before querying — no raw PII is stored.
        </p>

        {/* Search form */}
        <form onSubmit={handleSearch} className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div className="relative group">
            <div className="absolute inset-0 rounded-2xl bg-electric-cyan/5 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 blur-xl" />
            <div className="relative flex items-center gap-3 p-2 rounded-2xl border border-white/[0.08] bg-surface/60 backdrop-blur-xl group-focus-within:border-electric-cyan/30 transition-all duration-300">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-electric-cyan/10">
                <Search className="w-5 h-5 text-electric-cyan" />
              </div>
              <input
                ref={inputRef}
                type="email"
                placeholder="Enter email address to search…"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent text-white text-sm placeholder:text-text-muted outline-none py-3"
                autoFocus
              />
              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-electric-cyan to-cyan-dim text-deep-charcoal font-bold text-sm transition-all duration-300 hover:shadow-[0_4px_20px_rgba(0,245,255,0.3)] hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:hover:scale-100 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-deep-charcoal border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Search'
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Error */}
        {error && (
          <div className="mt-6 flex items-center gap-3 px-5 py-4 rounded-xl border border-red-500/20 bg-red-500/5 animate-fade-in">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <span className="text-sm text-red-400">{error}</span>
          </div>
        )}

        {/* Results */}
        {searched && results !== null && (
          <div className="mt-8 animate-slide-in-right">
            {/* Results header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-electric-cyan" />
                <span className="text-sm font-mono text-text-secondary">
                  {results.length} record{results.length !== 1 ? 's' : ''} found
                </span>
              </div>
              <span className="text-xs font-mono text-text-muted">
                {email.trim().toLowerCase()}
              </span>
            </div>

            {results.length === 0 ? (
              <div className="glass-card p-12 text-center animate-fade-in">
                <Hash className="w-10 h-10 text-text-muted/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-text-secondary">No Records</h3>
                <p className="text-text-muted text-sm">
                  No compliance log entries found for this email address.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {results.map((record, idx) => {
                  const isExpanded = expandedId === record.id;
                  return (
                    <div
                      key={record.id}
                      className="rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-sm overflow-hidden transition-all duration-300 hover:border-white/[0.12] animate-fade-in-up"
                      style={{ animationDelay: `${idx * 0.05}s` }}
                    >
                      {/* Summary */}
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : record.id)}
                        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold uppercase tracking-wider border ${getActionColor(record.action)}`}>
                            {record.action.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-text-muted font-mono truncate">
                            {formatDate(record.createdAt)}
                          </span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-text-muted shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className="border-t border-white/5 px-5 py-4 bg-white/[0.015] animate-fade-in">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <div className="flex items-center gap-1.5 mb-1">
                                <Hash className="w-3 h-3 text-text-muted" />
                                <span className="text-[10px] font-mono text-text-muted uppercase tracking-wider">ID</span>
                              </div>
                              <p className="text-xs text-white font-mono">{record.id}</p>
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5 mb-1">
                                <Clock className="w-3 h-3 text-text-muted" />
                                <span className="text-[10px] font-mono text-text-muted uppercase tracking-wider">Timestamp</span>
                              </div>
                              <p className="text-xs text-white font-mono">{formatDate(record.createdAt)}</p>
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5 mb-1">
                                <Shield className="w-3 h-3 text-text-muted" />
                                <span className="text-[10px] font-mono text-text-muted uppercase tracking-wider">Principal (Email)</span>
                              </div>
                              <p className="text-xs text-white font-mono">{email.trim().toLowerCase()}</p>
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5 mb-1">
                                <Globe className="w-3 h-3 text-text-muted" />
                                <span className="text-[10px] font-mono text-text-muted uppercase tracking-wider">IP Address</span>
                              </div>
                              <p className="text-xs text-white font-mono">{record.ipAddress}</p>
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5 mb-1">
                                <Activity className="w-3 h-3 text-text-muted" />
                                <span className="text-[10px] font-mono text-text-muted uppercase tracking-wider">Action</span>
                              </div>
                              <p className="text-xs text-white font-mono">{record.action}</p>
                            </div>
                            <div className="sm:col-span-2">
                              <div className="flex items-center gap-1.5 mb-1">
                                <FileSearch className="w-3 h-3 text-text-muted" />
                                <span className="text-[10px] font-mono text-text-muted uppercase tracking-wider">Description</span>
                              </div>
                              <p className="text-sm text-text-secondary leading-relaxed">{record.description}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Empty state before search */}
        {!searched && !loading && (
          <div className="mt-16 text-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="w-16 h-16 rounded-2xl bg-surface-light/50 flex items-center justify-center mx-auto mb-4">
              <FileSearch className="w-8 h-8 text-text-muted/40" />
            </div>
            <p className="text-text-muted text-sm font-mono">
              Enter an email to query the compliance ledger
            </p>
            <p className="text-text-muted/60 text-xs mt-2 max-w-sm mx-auto">
              The email is normalized (trimmed, lowercased) and SHA-256 hashed before lookup. Results show all stored fields with the hash replaced by the searched email.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
